import pdfMake from 'pdfmake/build/pdfmake';
import Logo from 'Icons/RMlogo.png';
import {
  BatchLoadExperimentsDTO,
  BatchLoadExperimentsData,
} from 'business/models/Models';
import ScreenService from 'business/services/ScreenService';
import ProteinFormulationService from 'business/services/ProteinFormulationService';
import { isNullOrUndefined, Round } from 'utils/Global';
import ScoreGroupService from 'business/services/PlateService';
import { createPdfWithPopupBlockerHandler } from './utils';
import { fonts } from './ReportsConstants';

pdfMake.fonts = { Roboto: fonts.Roboto };

export const TABLE_LAYOUT = {
  hLineWidth: function (i, node) {
    return 0.5;
  },
  vLineWidth: function (i, node) {
    return 0.5;
  },
};

export async function generateBatchLoadExperimentReportPDF(
  batchLoadExperigmentsData: BatchLoadExperimentsData[],
  experimentIds: number[],
  fileName: string,
  batchLoadExperigmentsDto: BatchLoadExperimentsDTO,
  onPopupBlocked?: () => void,
) {
  const pdfDefinition = {
    info: {
      title: 'BatchLoad Experiment Report',
      author: 'Rock Maker',
      subject: 'Experiment',
    },
    content: await CreateContent(
      batchLoadExperigmentsData,
      experimentIds,
      fileName,
      batchLoadExperigmentsDto,
    ),
    styles: {
      headerText: { fontSize: 15, bold: true, lineHeight: 1.5 },
      largeText: { fontSize: 12, lineHeight: 1.5 },
      labelText: { fontSize: 10, lineHeight: 1.5 },
      tableSubHeader: { bold: true, fillColor: '#cccccc' },
      tableAltRow: { fillColor: '#f5f5f5' },
      textBold: { bold: true },
    },
    defaultStyle: {
      fontSize: 8,
      lineHeight: 1.5,
    },
    pageMargins: [50, 50, 50, 50],
    header: getHeader(),
    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    fileName,
    false,
    onPopupBlocked,
  );
}

async function CreateContent(
  batchLoadExperigmentsData: BatchLoadExperimentsData[],
  experimentIds: number[],
  fileName: string,
  batchLoadExperimentsDto: BatchLoadExperimentsDTO,
) {
  const content = [];
  content.push({
    text: 'Batch Load Experiment Report',
    alignment: 'center',
    style: 'headerText',
  });
  const experimentsData = [];
  let expCounter = 0;

  for (const data of batchLoadExperigmentsData) {
    let experimentData = {} as any;
    experimentData = data;
    const screenService = new ScreenService();
    experimentData.screenData = await screenService.getScreenInfoByName(
      data.screen.value.trim(),
    );
    if (isNullOrUndefined(experimentData.screenData)) {
      console.warn(
        `BatchLoadExperimentReport: no screen found for name "${data.screen.value.trim()}".`,
      );
      experimentData.screenData = { screenName: 'N/A', screenPlateBarcode: 'N/A' };
    }
    const pfService = new ProteinFormulationService();
    experimentData.sampleData = await pfService.getProteinFormulationInfoByName(
      data.sample.value.trim(),
    );
    if (isNullOrUndefined(experimentData.sampleData)) {
      console.warn(
        `BatchLoadExperimentReport: no protein formulation found for sample "${data.sample.value.trim()}".`,
      );
      experimentData.sampleData = { pfId: null, pfName: 'N/A', pfIngredientName: 'N/A' };
    }
    experimentData.expId = experimentIds[expCounter];
    const plateService = new ScoreGroupService();
    experimentData.plateData = await plateService.getPlateInfoByExperimentId(
      experimentIds[expCounter],
    );
    if (isNullOrUndefined(experimentData.plateData)) {
      console.warn(
        `BatchLoadExperimentReport: no plate found for experiment id ${experimentIds[expCounter]}.`,
      );
      experimentData.plateData = {
        plateId: null,
        barcode: 'N/A',
        setupTemp: null,
        expPreparationDate: new Date(),
      };
    }
    experimentsData.push(experimentData);
    expCounter++;
  }

  content.push('\n');
  content.push(
    await getExperimentSummaryTable(fileName, experimentsData, batchLoadExperimentsDto),
  );
  content.push('\n');
  content.push(await getSupplySummaryTable(experimentsData, batchLoadExperimentsDto));
  content.push('\n');
  content.push(await getSampleUsageTable(experimentsData));
  content.push('\n');
  content.push(await getScreenUsageTable(experimentsData));
  content.push('\n');
  content.push(await getCrystallizationPlateTable(experimentsData));
  return content;
}

async function getExperimentSummaryTable(
  fileName: string,
  experimentsData: any[],
  batchLoadExperigmentsDto: BatchLoadExperimentsDTO,
) {
  const folders = [];
  for (const data of experimentsData) {
    if (folders.includes(data.folder.value.trim()) != true) {
      folders.push(data.folder.value.trim());
    }
  }

  return {
    table: {
      widths: ['35%', '15%', '10%', '10%', '10%', '10%', '10%'],
      body: [
        [
          {
            text: 'Experiment Summary:',
            bold: true,
            colSpan: 7,
            fillColor: '#808080',
            color: 'white',
            style: 'labelText',
          },
          {},
          {},
          {},
          {},
          {},
          {},
        ],
        [
          { text: 'Batch File ', bold: true, fillColor: '#cccccc' },
          { text: 'Submitted ', bold: true, fillColor: '#cccccc' },
          { text: 'Experiments ', bold: true, fillColor: '#cccccc' },
          { text: 'Folders ', bold: true, fillColor: '#cccccc' },
          { text: 'Projects ', bold: true, fillColor: '#cccccc' },
          { text: 'Samples ', bold: true, fillColor: '#cccccc' },
          { text: 'Plates ', bold: true, fillColor: '#cccccc' },
        ],
        [
          { text: fileName },
          {
            text: experimentsData[0].plateData.expPreparationDate
              .toLocaleString()
              .split(',')[0],
          },
          { text: batchLoadExperigmentsDto.experimentCount },
          { text: folders.length },
          { text: batchLoadExperigmentsDto.projectCount },
          { text: batchLoadExperigmentsDto.sampleCount },
          { text: batchLoadExperigmentsDto.experimentCount },
        ],
        [
          {
            text: [
              { text: 'Job Ext ID:', bold: true },
              { text: ' ' + experimentsData[0].user.value.trim() },
            ],
            colSpan: 7,
          },
          {},
          {},
          {},
          {},
          {},
          {},
        ],
      ],
    },
    layout: TABLE_LAYOUT,
  };
}

async function getSupplySummaryTable(
  experimentsData: any[],
  batchLoadExperimentsDto: BatchLoadExperimentsDTO,
) {
  const screens = [];
  const plates = [];
  for (const data of experimentsData) {
    if (screens.includes(data.screen.value.trim()) != true) {
      screens.push(data.screen.value.trim());
    }
    plates.push(data.plateData);
  }

  const screensCount = screens.length;
  const platesCount = plates.length;

  const body: any = [
    [
      {
        text: 'Supply Summary:',
        bold: true,
        colSpan: 3,
        fillColor: '#808080',
        color: 'white',
        style: 'labelText',
      },
      {},
      {},
    ],
    [
      { text: 'Plate Count ', bold: true, fillColor: '#cccccc' },
      { text: 'Screen Count ', bold: true, fillColor: '#cccccc' },
      { text: 'Sample Count ', bold: true, fillColor: '#cccccc' },
    ],
    [
      { text: platesCount },
      { text: screensCount },
      { text: batchLoadExperimentsDto.sampleCount },
    ],
  ];

  return {
    table: {
      widths: ['34%', '33%', '33%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getSampleUsageTable(experimentData: any[]) {
  const body: any = [
    [
      {
        text: 'Sample Usage:',
        bold: true,
        colSpan: 5,
        fillColor: '#808080',
        color: 'white',
        style: 'labelText',
      },
      {},
      {},
      {},
      {},
    ],
    [
      { text: 'Item ', bold: true, fillColor: '#cccccc' },
      { text: 'Sample ID ', bold: true, fillColor: '#cccccc' },
      { text: 'Sample Name ', bold: true, fillColor: '#cccccc' },
      { text: 'Sample Ingredients ', bold: true, fillColor: '#cccccc' },
      { text: 'Used Vol ', bold: true, fillColor: '#cccccc' },
    ],
  ];

  let itemNumber = 0;
  const wellCount = 96;

  const proteinFormulations: [
    number, // Sample number/index in the table
    number, // Protein Formulation ID
    string, // Protein Formulation Name
    string, // Protein Formulation Ingredient Name
    number, // Protein Formulation Volume
  ][] = [];

  for (const data of experimentData) {
    const pfName = data.sampleData.pfName;
    const volume = wellCount * Number(data.sampleWellRatio.value.trim());
    const existingPf = proteinFormulations.find((pf) => pf[2] === pfName);

    if (existingPf) {
      existingPf[4] = existingPf[4] + volume;
    } else {
      proteinFormulations.push([
        ++itemNumber,
        data.sampleData.pfId,
        pfName,
        data.sampleData.pfIngredientName,
        volume,
      ]);
    }
  }

  for (const [itemNumber, pfId, pfName, pfIngName, volume] of proteinFormulations) {
    body.push([itemNumber, pfId, pfName, pfIngName, Round(volume) + ' uL']);
  }

  return {
    table: {
      widths: ['20%', '20%', '20%', '20%', '20%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getScreenUsageTable(experimentData: any[]) {
  const body: any = [
    [
      {
        text: 'Screen Usage: ',
        bold: true,
        colSpan: 3,
        fillColor: '#808080',
        color: 'white',
        style: 'labelText',
      },
      {},
      {},
    ],
    [
      { text: 'Item ', bold: true, fillColor: '#cccccc' },
      { text: 'Screen ', bold: true, fillColor: '#cccccc' },
      { text: 'Barcode ', bold: true, fillColor: '#cccccc' },
    ],
  ];

  let itemNumber = 0;
  for (const data of experimentData) {
    if (isNullOrUndefined(body.find((x) => x[1] == data.screenData.screenName))) {
      itemNumber++;
      body.push([
        itemNumber,
        data.screenData.screenName,
        data.screenData.screenPlateBarcode,
      ]);
    }
  }

  return {
    table: {
      widths: ['34%', '33%', '33%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getCrystallizationPlateTable(experimentsData: any[]) {
  const body: any = [
    [
      {
        text: 'Crystallization Plates: ',
        bold: true,
        colSpan: 10,
        fillColor: '#808080',
        color: 'white',
        style: 'labelText',
      },
      {},
      {},
      {},
      {},
      {},
      {},
      {},
      {},
      {},
    ],
    [
      { text: 'Item ', bold: true, fillColor: '#cccccc' },
      { text: 'Plate ID ', bold: true, fillColor: '#cccccc' },
      { text: 'Barcode ', bold: true, fillColor: '#cccccc' },
      { text: 'Folder ', bold: true, fillColor: '#cccccc' },
      { text: 'Project ', bold: true, fillColor: '#cccccc' },
      { text: 'Sample ID ', bold: true, fillColor: '#cccccc' },
      { text: 'Screen ', bold: true, fillColor: '#cccccc' },
      { text: 'Temp ', bold: true, fillColor: '#cccccc' },
      { text: 'Drop Vol nl ', bold: true, fillColor: '#cccccc' },
      { text: 'Sample% ', bold: true, fillColor: '#cccccc' },
    ],
  ];

  let itemNumber = 0;
  for (const data of experimentsData) {
    itemNumber++;
    body.push([
      itemNumber,
      data.plateData.plateId,
      data.plateData.barcode,
      data.folder.value.trim(),
      data.project.value.trim(),
      data.sampleData.pfId,
      data.screenData.screenName,
      data.plateData.setupTemp,
      data.dropVolnl.value.trim(),
      data.samplePercentage.value.trim(),
    ]);
  }

  return {
    table: {
      widths: ['6%', '10%', '10%', '10%', '10%', '10%', '18%', '6%', '10%', '10%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

function getHeader() {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 4, 0, 0],
          width: 400,
          fontSize: 12,
          color: '#707070',
        },
        {
          text: 'Printed On: ' + new Date().toLocaleDateString('en-US'),
          margin: [0, 5, 0, 0],
          color: '#707070',
        },
      ],
      margin: [20, 20, 20, 20],
    },
  ];
}
